import { boundedText } from "./bounded-text.js";
// The compact summary a concierge reads BEFORE accepting a handoff (CHT-734), so they need not read the whole thread.
// It is built from structured facts by plain code, with no model: the same input gives the same summary. The facts are
// codes and ids (they go to `handoff_summary_snapshots.facts`, bounded); the text is staff-internal and goes to
// `summary_text` (at most 2,000 characters). The text may hold the customer's own words (the staff-internal reason
// summary of the handoff and the titles of their open requests), so it has ONE read path, for staff who may read the
// handoff. It is never in a customer view, an audit row, a receipt, a log, an attention event or a metric.
export const HANDOFF_SUMMARY_TEXT_MAX = 2000;
/** The facts are a few codes and at most five ids: this is far above the real size and far below a risk. */
export const HANDOFF_SUMMARY_FACTS_MAX_BYTES = 4096;
const MAX_OPEN_REQUESTS_IN_FACTS = 5;
const WORDS_TITLE = 80;
const WORDS_CUSTOMER = 400;
export const handoffSummaryGenerators = Object.freeze(["SYSTEM", "COPILOT", "STAFF"]);
/** Why a person is needed, in one plain sentence per reason code. Staff-internal wording. */
const WHY_HUMAN = Object.freeze({
    CUSTOMER_REQUESTED_PERSON: "The customer asked to talk to a person.",
    SUPPLIER_CONTACT_REQUIRED: "A supplier must be contacted by a person.",
    CONCIERGE_JUDGMENT_REQUIRED: "The request needs a concierge's judgment.",
    CATALOG_DATA_CONFLICT: "The catalog data conflicts and a person must decide.",
    UNSUPPORTED_HIGH_IMPACT_REQUEST: "The request is high impact and the assistant may not handle it.",
    COMPLAINT_SERVICE_RECOVERY: "The customer has a complaint and needs service recovery.",
    PAYMENT_FULFILMENT_EXCEPTION: "A payment or fulfilment exception needs a person.",
    REPEATED_NO_PROGRESS: "The assistant made no progress after repeated tries.",
    POLICY_REVIEW_REQUIRED: "A policy review is required before anything goes on.",
});
// A C0 control (not the newline), DEL or a C1 control becomes a space: the text column refuses them.
// biome-ignore lint/suspicious/noControlCharactersInRegex: the whole point is to remove control characters
const CONTROL = /[\u0000-\u0009\u000b-\u001f\u007f-\u009f]/g;
function oneLine(value, max) {
    const clean = value.replace(CONTROL, " ").replace(/\s+/g, " ").trim();
    const chars = [...clean];
    return chars.length <= max ? clean : `${chars.slice(0, max - 1).join("")}…`;
}
/** The deterministic summary of a handoff. The same input always gives the same facts and the same text. */
export function buildHandoffSummary(input) {
    const listed = input.openRequests.slice(0, MAX_OPEN_REQUESTS_IN_FACTS);
    const facts = Object.freeze({
        v: 1,
        reasonCode: input.reasonCode,
        requestedBy: input.requestedBy,
        hasCustomerWords: input.reasonSummary !== null && input.reasonSummary.trim() !== "",
        openRequestCount: input.openRequests.length,
        openRequests: Object.freeze(listed.map((request) => Object.freeze({ id: request.id, requestType: request.requestType, state: request.state }))),
        openPaymentRequests: input.openPaymentRequests,
        preferenceCodes: Object.freeze({ ...input.preferences }),
        activeTripId: input.activeTripId,
    });
    const lines = [`Why a person is needed: ${WHY_HUMAN[input.reasonCode]}`];
    if (facts.hasCustomerWords && input.reasonSummary !== null) {
        lines.push(`In the customer's words: ${oneLine(input.reasonSummary, WORDS_CUSTOMER)}`);
    }
    if (input.openRequests.length === 0) {
        lines.push("Open requests: none.");
    }
    else {
        lines.push(`Open requests: ${input.openRequests.length}.`);
        for (const request of listed) {
            lines.push(`- ${request.requestType} (${request.state}): ${oneLine(request.title, WORDS_TITLE)}`);
        }
        if (input.openRequests.length > listed.length) {
            lines.push(`- and ${input.openRequests.length - listed.length} more.`);
        }
    }
    if (input.openPaymentRequests > 0) {
        lines.push(`Payments waiting for the customer: ${input.openPaymentRequests}.`);
    }
    const preferences = [
        input.preferences.locale === null ? null : `language ${input.preferences.locale}`,
        input.preferences.homeMarket === null ? null : `market ${input.preferences.homeMarket}`,
        input.preferences.contactChannel === null
            ? null
            : `contact channel ${input.preferences.contactChannel}`,
    ].filter((part) => part !== null);
    if (preferences.length > 0)
        lines.push(`Preferences: ${preferences.join(", ")}.`);
    if (input.activeTripId !== null)
        lines.push("The customer has a trip in progress.");
    const text = lines.join("\n");
    const chars = [...text];
    const summaryText = chars.length <= HANDOFF_SUMMARY_TEXT_MAX
        ? text
        : `${chars.slice(0, HANDOFF_SUMMARY_TEXT_MAX - 1).join("")}…`;
    return Object.freeze({ facts, summaryText });
}
/**
 * The text a copilot or a staff member appends as a newer snapshot: trimmed, 1 to 2,000 characters, no control
 * characters (a newline is allowed). The message names the rule, never the text.
 */
export function handoffSummaryText(value) {
    return boundedText("summaryText", value, HANDOFF_SUMMARY_TEXT_MAX, { multiline: true });
}
